import { z } from 'zod';
import { VistarWidget } from './VistarWidget';
import { WidgetType, ZodBaseWidgetConfiguration, TimelineSlot } from '../../types';
import { BaseThirdPartyAd, ZodBaseThirdPartyAdWidgetConfigurationDetails } from '../types';
import { AxiosError } from 'axios';

export type VistarAd = BaseThirdPartyAd & {
    displayTime: number;

    /**
     * A url that is called to send proof of play
     * of this ad's creative to Vistar servers.
     */
    popUrl: string;
};

/**
 * Schema mirroring the JSON response of Vistar's ad API
 * @see {@link https://help.vistarmedia.com/hc/en-us/articles/225058628-Ad-Serving-API#adreq}
 */
export const vistarApiFetchAdsResponseSchema = z.object({
    advertisement: z.array(
        z.object({
            id: z.string(),
            proof_of_play_url: z.string(),
            lease_expiry: z.number(),
            asset_url: z.string(),
            width: z.number(),
            height: z.number(),
            mime_type: z.string(),
            expiration_url: z.string(),
            length_in_seconds: z.number().optional(),
        })
    ),
});

export type VistarAPIFetchAdsResponse = z.infer<typeof vistarApiFetchAdsResponseSchema>;

/**
 * @example Sample of JSON response Vistar API returns upon error
 * ```json
 * {
 *   "status": 400,
 *   "error": "venue_id not found"
 * }
 * ```
 */
export type VistarError = AxiosError<{ error: string }>;

export type VistarWidgetTimelineSlotParams = {
    ad: VistarAd;
};

export type VistarWidgetTimelineSlot = TimelineSlot<VistarWidgetTimelineSlotParams, VistarWidget>;

export const ZodVistarWidgetConfigurationDetails =
    ZodBaseThirdPartyAdWidgetConfigurationDetails.extend({
        venue_id: z.string().min(1),
    });

export type VistarWidgetConfigurationDetails = z.infer<typeof ZodVistarWidgetConfigurationDetails>;

export const ZodVistarWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    type: z.literal(WidgetType.VISTAR),
    config: ZodVistarWidgetConfigurationDetails,
});

export type VistarWidgetConfiguration = z.infer<typeof ZodVistarWidgetConfiguration>;
